import './area.erb';
import './index.erb';
import './index.rb';
import './redirect.erb';
import ModalManager from 'utils/modal_manager';
import { fetchJanusOabRedirectUrl } from 'utils/janus_oab_redirect';
import { fetchProenemRedirectUrl } from 'utils/proenem_redirect';
import {
  buildProenemSourcePath,
  clearProenemSetupRedirectContext,
  getProenemSetupRedirectContext
} from 'utils/proenem_setup_redirect_context';

const MAPPING_PRODUCTS = {
  concursos: 'Concurso Público',
  militares: 'Forças Armadas',
  oab: 'OAB',
  vestibular: 'Enem e Vestibular'
};
const JANUS_OAB_REDIRECT_TYPE = 'janus_oab_sso';
const JANUS_OAB_QUESTION_SSO_TYPE = 'janus_oab_question_sso';
const JANUS_OAB_ICON = 'oab-damasio';
const PROENEM_REDIRECT_TYPE = 'proenem_sso';
const PROENEM_ICON = 'proenem';
const PROENEM_SSO_ERROR_MESSAGE =
  'Não foi possível conectar ao ProEnem. Tente novamente em instantes.';

export class Setup {
  constructor() {
    this.$context = $('#js-setup');
    this.$productTypes = $('#products', this.$context);
    this.$buttons = $('#products > button', this.$context);
    this.$buttonsOptions = $('#options > button', this.$context);
    this.$btnNextStep = $('#btnSetupNextStep', this.$context);

    this.$areaContent = $('#area', this.$context);
    this.$redirectContent = $('#redirect', this.$context);
    this.$loadingContent = $('#loading', this.$context);

    this.$selected = null;
    this.isRedirecting = false;

    if (!this.$context.get(0)) {
      return;
    }

    this.products = this.$productTypes.data('products');
    this.platform = 'web';
    this.feature_version = '3.0.2';

    this.bindEvents();
    this.bindNextStep();
  }

  open = () => {
    if (!this.$context.get(0)) {
      return;
    }

    ModalManager.open(this.$context);

    // Para logica do onboarding guiado (experiment_guided_onboarding)
    const activationDate = new Date().toISOString();
    document.cookie = `activationDate=${encodeURIComponent(
      activationDate
    )}; domain=.qconcursos.com; path=/; max-age=1728000; SameSite=Lax`;

    window.UserTracker.emit('event', {
      eventName: 'account setup started',
      data: {
        platform: this.platform,
        feature_version: this.feature_version,
        request_path: window.location.pathname
      }
    });
  };

  bindEvents = () => {
    this.$buttons.on('click', e => {
      e.preventDefault();

      // toggle class "selected"
      this.$buttons.each(idx => $(this.$buttons[idx]).removeClass('selected'));

      $(e.currentTarget).addClass('selected');

      this.$selected = $(e.currentTarget);

      this.bindSelected();
      this.bindRedirect();
    });
  };

  bindRedirect = () => {
    this.$buttonsOptions
      .off('click.setupRedirect')
      .on('click.setupRedirect', async e => {
        e.preventDefault();

        if (this.isRedirecting) {
          return;
        }

        const params = new URLSearchParams(window.location.search);

        const current = $(e.currentTarget);
        const currentData = current.data();

        window.UserTracker.emit('event', {
          eventName: 'account setup completed',
          data: {
            'first action chosen': currentData.option,
            platform: this.platform,
            feature_version: this.feature_version,
            request_path: window.location.pathname,
            segment: MAPPING_PRODUCTS[this.$selected.data().product]
          }
        });

        let redirect = currentData.redirect;
        const _blank = currentData.blank;

        const questionId = this.shouldUseJanusOabQuestionSso(currentData)
          ? String(currentData.questionId)
          : null;

        if (this.shouldUseJanusOabSso(currentData)) {
          redirect = await fetchJanusOabRedirectUrl(questionId);

          if (!redirect) {
            return;
          }
        }

        if (this.shouldUseProenemSso(currentData)) {
          this.armProenemRedirectCta(current);

          try {
            window.UserTracker?.emit('event', {
              eventName: 'proenem redirect clicked',
              data: {
                'click source': 'setup modal antigo',
                'source url': window.location.href
              }
            });
          } catch (trackerErr) {
            window.Rollbar?.error(
              'Erro ao emitir evento proenem redirect clicked',
              trackerErr
            );
          }

          const proenemContext = getProenemSetupRedirectContext();
          const sourcePath = buildProenemSourcePath({
            questionCode: proenemContext?.questionCode,
            resolverSimulado: proenemContext?.resolverSimulado
          });
          redirect = await fetchProenemRedirectUrl(sourcePath);
          clearProenemSetupRedirectContext();

          if (!redirect) {
            this.releaseProenemRedirectCta(current);
            toastr.error(PROENEM_SSO_ERROR_MESSAGE);
            return;
          }
        }

        const isStudyGuideUrl = await this.checkStudyGuideUrl(redirect);

        if (isStudyGuideUrl) {
          return;
        }

        if (redirect.includes('app.qconcursos.com')) {
          const user_preferences = { default_environment: 'ils' };
          this.bindDefaultEnvironment(user_preferences);

          if (this.$loadingContent) {
            this.$areaContent.addClass('hidden');
            this.$redirectContent.addClass('hidden');

            if (!redirect) {
              this.$redirectContent.addClass('hidden');
            }
            this.$loadingContent.removeClass('hidden');

            setTimeout(() => {
              this.redirectUser(redirect, _blank);
            }, 2000);

            return;
          }
        }

        const finalRedirect =
          redirect === '' || redirect === null
            ? params.get('return_url')
            : redirect;

        if (finalRedirect) {
          this.redirectUser(finalRedirect, _blank);
        }

        ModalManager.close(this.$context);
      });
  };

  armProenemRedirectCta = $button => {
    this.isRedirecting = true;
    $button
      .data('original-html', $button.html())
      .prop('disabled', true)
      .attr('aria-busy', 'true')
      .addClass('is-loading')
      .text('Redirecionando...');
  };

  releaseProenemRedirectCta = $button => {
    this.isRedirecting = false;
    const originalHtml = $button.data('original-html');

    if (originalHtml) {
      $button.html(originalHtml);
    }

    $button
      .prop('disabled', false)
      .removeAttr('aria-busy')
      .removeClass('is-loading')
      .removeData('original-html');
  };

  bindDefaultEnvironment = async user_preferences => {
    await Thor.UserPreference.update(
      {},
      {
        data: { user_preferences }
      }
    );
  };

  bindSelected = () => {
    if (this.$selected) {
      this.$btnNextStep.removeAttr('disabled');
    }
  };

  bindNextStep = () => {
    this.$btnNextStep.on('click', e => {
      e.preventDefault();

      this.bindPreferences(this.$selected.data().product);

      window.UserTracker.emit('event', {
        eventName: 'account setup preferences setted',
        data: {
          platform: this.platform,
          feature_version: this.feature_version,
          request_path: window.location.pathname,
          segment: MAPPING_PRODUCTS[this.$selected.data().product]
        }
      });

      this.$areaContent.addClass('hidden');
      this.$redirectContent.removeClass('hidden');

      this.handleVisibleButton();
    });
  };

  handleVisibleButton = () => {
    const selectedProduct = this.$selected.data().product;

    this.$buttonsOptions.each(function () {
      const compatibleProducts = $(this).data('segment')?.split(',') || [];

      compatibleProducts.includes(selectedProduct)
        ? $(this).show()
        : $(this).hide();
    });
  };

  getProduct = key => {
    return this.products.find(item => item.key === key);
  };

  getProductId = key => {
    const product = this.getProduct(key);
    return product?.id;
  };

  bindPreferences = async product => {
    const user_preferences = {
      chose_product: true,
      preferred_product_id: this.getProductId(product)
    };

    try {
      await Thor.UserPreference.update(
        {},
        {
          data: { user_preferences }
        }
      );

      toastr.success('Preferências salvas com sucesso!');
    } catch (err) {
      const message = 'Tivemos um problema ao tentar enviar suas preferências!';

      toastr.error(message);
      Rollbar.error(message, err);
    }
  };

  redirectUser = (redirectUrl, openInBlank) => {
    if (openInBlank) {
      window.open(redirectUrl, '_blank');
      return;
    }

    if (this.isExternalUrl(redirectUrl)) {
      window.location.href = redirectUrl;
      return;
    }

    window.Turbolinks.visit(redirectUrl);
  };

  isExternalUrl = redirectUrl => {
    const parsedUrl = new URL(redirectUrl, window.location.origin);
    return parsedUrl.origin !== window.location.origin;
  };

  shouldUseJanusOabSso = currentData => {
    return (
      currentData.redirectType === JANUS_OAB_REDIRECT_TYPE ||
      currentData.icon === JANUS_OAB_ICON ||
      this.shouldUseJanusOabQuestionSso(currentData)
    );
  };

  shouldUseJanusOabQuestionSso = currentData => {
    return (
      currentData.redirectType === JANUS_OAB_QUESTION_SSO_TYPE &&
      currentData.questionId !== undefined &&
      currentData.questionId !== ''
    );
  };

  shouldUseProenemSso = currentData => {
    return (
      currentData.redirectType === PROENEM_REDIRECT_TYPE ||
      currentData.icon === PROENEM_ICON
    );
  };

  checkStudyGuideUrl = async redirectUrl => {
    if (!redirectUrl) {
      return false;
    }

    const studyGuidePattern = /\/b\/guia-de-estudos\/(\d+)\/videoaulas/;
    const match = redirectUrl.match(studyGuidePattern);

    if (match) {
      const studyGuideId = match[1];
      await this.linkUserToStudyGuide(studyGuideId, redirectUrl);
      return true;
    }

    return false;
  };

  linkUserToStudyGuide = async (studyGuideId, redirectUrl) => {
    try {
      await Thor.StudyGuides.favorite(
        {},
        {
          data: { study_guide_id: studyGuideId }
        }
      );

      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    } catch (err) {
      Rollbar.error('Erro ao vincular usuário ao guia de estudos', err);
    }
  };
}

(() => {
  document.addEventListener('turbolinks:load', function () {
    const setupModal = new Setup();
    setupModal.open();
  });
})();
